import { useState } from 'react';
import { clsx } from 'clsx';
import type { Race, Subrace } from '@character-creator/types';
import { useCharacterCreationStore } from '../../../stores/characterCreationStore';
import { useRulesetRaces, useRulesetRace } from '../../../hooks/useRulesetData';
import { useRulesetTerminology } from '../../../hooks/useRulesetFeatures';
import { formatAbility } from '../../../utils/formatting';
import { DiceIcon } from '../../ui/Icons';

export function RaceStep() {
  const { draft, setRace, setSubrace, setAppearance, clearRace } = useCharacterCreationStore();
  const races = useRulesetRaces();
  const selectedRace = useRulesetRace(draft.raceId);
  const terminology = useRulesetTerminology();

  const handleSuggest = () => {
    if (races.length === 0) return;
    const randomRace = races[Math.floor(Math.random() * races.length)];
    setRace(randomRace.id);
    // If the race has subraces, pick a random one
    if (randomRace.subraces && randomRace.subraces.length > 0) {
      const randomSubrace = randomRace.subraces[Math.floor(Math.random() * randomRace.subraces.length)];
      setSubrace(randomSubrace.id);
    }
  };

  const hasAppearanceData = Object.keys(draft.appearance).length > 0;
  const [appearanceExpanded, setAppearanceExpanded] = useState(hasAppearanceData);
  const hasData = draft.raceId !== null || hasAppearanceData;

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
            Choose Your {terminology.race}
          </h2>
          <p className="text-text-secondary">
            Your {terminology.race.toLowerCase()} determines your physical traits, cultural background, and innate abilities.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasData && (
            <button
              type="button"
              onClick={clearRace}
              className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={handleSuggest}
            className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg bg-background-secondary text-text-primary hover:bg-background-elevated transition-colors flex items-center gap-1.5"
          >
            <DiceIcon className="w-4 h-4" />
            Suggest
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Race/Species Selection */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4">{terminology.racePlural}</h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {races.map((race) => (
              <RaceCard
                key={race.id}
                race={race}
                isSelected={draft.raceId === race.id}
                onClick={() => setRace(race.id)}
              />
            ))}
          </div>
        </div>

        {/* Race Details / Subrace Selection */}
        <div>
          {selectedRace ? (
            <RaceDetails
              race={selectedRace}
              selectedSubraceId={draft.subraceId}
              onSelectSubrace={setSubrace}
            />
          ) : (
            <div className="card p-6 text-center text-text-muted">
              <p>Select a {terminology.race.toLowerCase()} to see details</p>
            </div>
          )}
        </div>
      </div>

      {/* Physical Appearance */}
      <div className="card p-6 mt-6">
        <button
          type="button"
          onClick={() => setAppearanceExpanded(!appearanceExpanded)}
          aria-expanded={appearanceExpanded}
          className="w-full flex items-center justify-between"
        >
          <h3 className="font-semibold text-text-primary">
            Physical Appearance <span className="text-text-muted font-normal">(optional)</span>
          </h3>
          <div className="flex items-center gap-2">
            {!appearanceExpanded && hasAppearanceData && (
              <span className="text-sm text-accent-gold">
                {Object.values(draft.appearance).filter(Boolean).length} fields set
              </span>
            )}
            <span className={clsx(
              'text-text-muted text-xs transition-transform duration-200',
              appearanceExpanded && 'rotate-90'
            )}>
              ▶
            </span>
          </div>
        </button>
        <div className={clsx(
          'overflow-hidden transition-all duration-200 ease-in-out',
          appearanceExpanded ? 'max-h-[400px] opacity-100 mt-4' : 'max-h-0 opacity-0'
        )}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AppearanceField
              label="Age"
              value={draft.appearance.age || ''}
              onChange={(value) => setAppearance({ age: value })}
              placeholder="e.g., 25 years"
            />
            <AppearanceField
              label="Height"
              value={draft.appearance.height || ''}
              onChange={(value) => setAppearance({ height: value })}
              placeholder="e.g., 5 ft 10 in"
            />
            <AppearanceField
              label="Weight"
              value={draft.appearance.weight || ''}
              onChange={(value) => setAppearance({ weight: value })}
              placeholder="e.g., 160 lbs"
            />
            <AppearanceField
              label="Eyes"
              value={draft.appearance.eyes || ''}
              onChange={(value) => setAppearance({ eyes: value })}
              placeholder="e.g., Green"
            />
            <AppearanceField
              label="Skin"
              value={draft.appearance.skin || ''}
              onChange={(value) => setAppearance({ skin: value })}
              placeholder="e.g., Tan"
            />
            <AppearanceField
              label="Hair"
              value={draft.appearance.hair || ''}
              onChange={(value) => setAppearance({ hair: value })}
              placeholder="e.g., Brown, long"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface AppearanceFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

function AppearanceField({ label, value, onChange, placeholder }: AppearanceFieldProps) {
  return (
    <div>
      <label className="block text-sm text-text-muted mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="input"
        maxLength={50}
      />
    </div>
  );
}

interface RaceCardProps {
  race: Race;
  isSelected: boolean;
  onClick: () => void;
}

function RaceCard({ race, isSelected, onClick }: RaceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full text-left p-4 rounded-lg border transition-all duration-200',
        isSelected
          ? 'bg-accent-gold/10 border-accent-gold'
          : 'bg-background-card border-background-elevated hover:border-text-muted'
      )}
    >
      <div className="flex items-center justify-between">
        <span className={clsx(
          'font-semibold',
          isSelected ? 'text-accent-gold' : 'text-text-primary'
        )}>
          {race.name}
        </span>
        <span className="text-sm text-text-muted">
          +{race.abilityBonuses.map(b => `${b.bonus} ${b.ability.slice(0, 3).toUpperCase()}`).join(', ')}
        </span>
      </div>
      <p className="text-sm text-text-secondary mt-1 line-clamp-2">
        {race.description}
      </p>
    </button>
  );
}

interface RaceDetailsProps {
  race: Race;
  selectedSubraceId: string | null;
  onSelectSubrace: (id: string | null) => void;
}

function RaceDetails({ race, selectedSubraceId, onSelectSubrace }: RaceDetailsProps) {
  const selectedSubrace = race.subraces?.find(s => s.id === selectedSubraceId);

  return (
    <div className="card p-6 space-y-6">
      <div>
        <h3 className="font-display text-xl font-bold text-accent-gold mb-2">
          {race.name}
        </h3>
        <p className="text-text-secondary">{race.description}</p>
      </div>

      {/* Ability Bonuses */}
      <div>
        <h4 className="text-sm font-semibold text-text-primary mb-2">Ability Score Increase</h4>
        <div className="flex flex-wrap gap-2">
          {race.abilityBonuses.map((bonus) => (
            <span
              key={bonus.ability}
              className="px-2 py-1 bg-accent-gold/10 text-accent-gold rounded text-sm"
            >
              +{bonus.bonus} {formatAbility(bonus.ability)}
            </span>
          ))}
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-text-muted">Size:</span>
          <span className="ml-2 text-text-primary capitalize">{race.size}</span>
        </div>
        <div>
          <span className="text-text-muted">Speed:</span>
          <span className="ml-2 text-text-primary">{race.speed} ft.</span>
        </div>
        <div className="col-span-2">
          <span className="text-text-muted">Languages:</span>
          <span className="ml-2 text-text-primary">{race.languages.join(', ')}</span>
        </div>
      </div>

      {/* Traits */}
      {race.traits.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-2">Racial Traits</h4>
          <ul className="space-y-2">
            {race.traits.map((trait) => (
              <li key={trait.id} className="text-sm">
                <span className="font-medium text-text-primary">{trait.name}.</span>{' '}
                <span className="text-text-secondary">{trait.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Subrace Selection */}
      {race.subraces && race.subraces.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-text-primary mb-2">
            Choose a Subrace
            <span className="text-error ml-1">*</span>
          </h4>
          <div className="space-y-2">
            {race.subraces.map((subrace) => (
              <SubraceCard
                key={subrace.id}
                subrace={subrace}
                isSelected={selectedSubraceId === subrace.id}
                onClick={() => onSelectSubrace(subrace.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Selected Subrace Details */}
      {selectedSubrace && (
        <div className="border-t border-background-elevated pt-4">
          <h4 className="text-sm font-semibold text-accent-gold mb-2">
            {selectedSubrace.name}
          </h4>
          <p className="text-sm text-text-secondary mb-3">{selectedSubrace.description}</p>

          {selectedSubrace.abilityBonuses.length > 0 && (
            <div className="mb-3">
              <span className="text-xs text-text-muted">Additional Ability Bonus:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedSubrace.abilityBonuses.map((bonus) => (
                  <span
                    key={bonus.ability}
                    className="px-2 py-0.5 bg-accent-gold/10 text-accent-gold rounded text-xs"
                  >
                    +{bonus.bonus} {formatAbility(bonus.ability)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {selectedSubrace.traits.length > 0 && (
            <ul className="space-y-1">
              {selectedSubrace.traits.map((trait) => (
                <li key={trait.id} className="text-sm">
                  <span className="font-medium text-text-primary">{trait.name}.</span>{' '}
                  <span className="text-text-secondary">{trait.description}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

interface SubraceCardProps {
  subrace: Subrace;
  isSelected: boolean;
  onClick: () => void;
}

function SubraceCard({ subrace, isSelected, onClick }: SubraceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'w-full text-left p-3 rounded-lg border transition-all duration-200',
        isSelected
          ? 'bg-accent-gold/10 border-accent-gold'
          : 'bg-background-secondary border-background-elevated hover:border-text-muted'
      )}
    >
      <div className="flex items-center justify-between">
        <span className={clsx(
          'font-medium',
          isSelected ? 'text-accent-gold' : 'text-text-primary'
        )}>
          {subrace.name}
        </span>
        {subrace.abilityBonuses.length > 0 && (
          <span className="text-xs text-text-muted">
            +{subrace.abilityBonuses.map(b => `${b.bonus} ${b.ability.slice(0, 3).toUpperCase()}`).join(', ')}
          </span>
        )}
      </div>
    </button>
  );
}


